import { createSlice } from "@reduxjs/toolkit";
import {groupBy} from "@/helpers/object";
import dayjs from "dayjs";
import {getBookingPackageIdFromSlots} from "@/helpers/bookings/getBookingPackageIdFromSlots";

export const calendarSlice = createSlice({
  name: "calendar",
  initialState: {
    viewType: "calendar",
    calendarData: [],
    calendarBookingGroups: {},
    editBookingData: null,
    staffOptions: [],
    filteredStaffIds: [],
  },
  reducers: {
    setViewType: (state, { payload }) => {
        state.viewType = payload;
    },
    setCalendarData(state, { payload }) {
      state.calendarData = payload
      state.calendarBookingGroups = groupBy(
        payload,
        (item) => item.type === 'waitlist' ? `waitlist-${item.id}` : item.booking_id
      );
    },
    simpleSetEditBookingData(state, { payload }) {
      state.editBookingData = payload;
    },
    setEditBookingData(state, { payload }) {
      if (!payload) {
        state.editBookingData = null;
        return;
      }

      const updatedGroup = [
        ...(state.calendarBookingGroups[payload.booking_id] || []),
        payload,
      ];

      state.calendarBookingGroups[payload.booking_id] = Array.from(
        new Map(updatedGroup.map((item) => [item.id, item])).values()
      );

      const allSlots = state.calendarBookingGroups[payload?.booking_id] ?? [];
      const validSlots = allSlots.filter((item) => item?.service?.id);
      const slotsForRange = validSlots.length ? validSlots : allSlots;

      const { minStart, maxEnd } = slotsForRange.reduce((acc, item) => {
        const start = dayjs(item.start_date_time);
        const end = dayjs(item.end_date_time);

        if (!start.isValid()) return acc;

        const currentStart = acc.minStart ? dayjs(acc.minStart) : null;
        const currentEnd = acc.maxEnd ? dayjs(acc.maxEnd) : null;

        if (!currentStart || start.isBefore(currentStart)) acc.minStart = start.format('YYYY-MM-DD HH:mm').toString();
        if (end.isValid() && (!currentEnd || end.isAfter(currentEnd))) {
          acc.maxEnd = end.format('YYYY-MM-DD HH:mm').toString();
        }

        return acc;
      }, { minStart: null, maxEnd: null });

      const servicePackageId = getBookingPackageIdFromSlots(allSlots, payload);

      const services = servicePackageId
        ? []
        : Array.from(
          validSlots.reduce((acc, booking) => {
            if (!booking?.service?.id) return acc;

            const start = dayjs(booking.start_date_time);
            const end = dayjs(booking.end_date_time);

            if (!start.isValid() || !end.isValid()) return acc;

            acc.set(booking.service.id, {
              service_id: booking.service.id,
              duration: end.diff(start, 'minute'),
              price: booking?.invoiceItem?.amount ?? booking?.service?.price ?? null,
            });

            return acc;
          }, new Map()).values()
        );

      const resolvedMaxEnd = (() => {
        if (!minStart) return maxEnd;

        if (servicePackageId) return maxEnd;

        if (!services.length) return maxEnd;

        const totalDuration = services.reduce((sum, service) => sum + (service.duration || 0), 0);
        if (!totalDuration) return maxEnd;

        return dayjs(minStart).add(totalDuration, 'minute').format('YYYY-MM-DD HH:mm');
      })();

      const customer = payload?.user;
      const orderPayload = payload?.booking?.payment?.order?.items?.[0]?.payload ?? null;

      state.editBookingData = {
        id: payload?.booking_id ?? null,
        status: payload?.booking?.status ?? null,
        type: payload?.booking?.type?.type ?? "admin",
        company_id: payload?.company_id ?? null,
        location_id: payload?.location_id ?? null,
        lang: payload?.locale ?? "en",
        trans: {
          en: {
            first_name: payload?.booking?.is_anonymous ? undefined : (customer?.trans?.en?.first_name ?? (customer?.first_name ?? "")),
            last_name: payload?.booking?.is_anonymous ? undefined : (customer?.trans?.en?.last_name ?? (customer?.last_name ?? "")),
              notes: (payload?.booking?.trans?.en?.notes ?? (payload?.booking?.notes ?? "")),
          },
          ru: {
            first_name: payload?.booking?.is_anonymous ? undefined : (customer?.trans?.ru?.first_name ?? (customer?.first_name ?? "")),
            last_name: payload?.booking?.is_anonymous ? undefined : (customer?.trans?.ru?.last_name ?? (customer?.last_name ?? "")),
            notes: (payload?.booking?.trans?.ru?.notes ?? (payload?.booking?.notes ?? "")),
          },
          hy: {
            first_name: payload?.booking?.is_anonymous ? undefined : (customer?.trans?.hy?.first_name ?? (customer?.first_name ?? "")),
            last_name: payload?.booking?.is_anonymous ? undefined : (customer?.trans?.hy?.last_name ?? (customer?.last_name ?? "")),
              notes: (payload?.booking?.trans?.hy?.notes ?? (payload?.booking?.notes ?? "")),
          },
        },
        email: payload?.booking?.is_anonymous ? undefined : customer?.email ?? undefined,
        phone: payload?.booking?.is_anonymous ? undefined : customer?.phone ?? undefined,
        nickname: payload?.booking?.is_anonymous ? undefined : customer?.locationInfo?.length && customer?.locationInfo[0].nickname ? customer?.locationInfo[0].nickname : undefined,
        is_anonymous: payload?.booking?.is_anonymous ?? false,
        end_date_time: resolvedMaxEnd ?? null,
        start_date_time: minStart ?? null,
        payment: payload?.payment ?? null,
        gift_card: payload?.payment?.giftCardUsage?.giftCardUser?.code ?? null,
        invoiceItem: payload?.invoiceItem ?? null,
        services,
        service_package_id: servicePackageId ?? undefined,
        [Symbol.for('service_options')]: Array.from(
          new Set(
            (state.calendarBookingGroups[payload?.booking_id] || [])
              .map(({ service }) => service)
              .filter((service) => service)
          )
        ),
        [Symbol.for('staff_options')]: [state.calendarBookingGroups[payload?.booking_id][0]?.staff].filter(staff => staff),
        [Symbol.for('customer_options')]: payload?.booking?.is_anonymous ? [] : customer ? [customer] : [],
        staff_id: payload?.staff?.id ?? null,
        customer_id: payload?.booking?.is_anonymous ? undefined : customer?.id ?? null,
        coupon: orderPayload?.coupon,
        packageSlotAssignments: payload?.booking?.packageSlotAssignments
          ?? (servicePackageId
            ? allSlots
                .filter((slot) => slot?.id && slot?.start_date_time)
                .map((slot) => ({
                  slot_id: slot.id,
                  start_date_time: slot.start_date_time,
                  end_date_time: slot.end_date_time,
                  is_paid: slot.is_paid ?? false,
                  service: slot.service ?? null,
                  staff: slot.staff ?? null,
                  invoice_item: slot.invoiceItem ?? null,
                }))
            : undefined),
      };
    },
    setStaffOptions(state, { payload }) {
      state.staffOptions = payload
    },
    setFilteredStaffIds(state, { payload }) {
      state.filteredStaffIds = payload
    }
  },
});

export const {
  setCalendarData,
  setStaffOptions,
  setFilteredStaffIds,
  setEditBookingData,
  setViewType,
  simpleSetEditBookingData,
} = calendarSlice.actions;
